import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { getTreatments } from '@/lib/api/treatments';
import { getTeam } from '@/lib/api/team';
import { getReviews } from '@/lib/api/reviews';

interface Booking {
  id: string;
  date: string;
  time: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  service: string;
  package: string;
  message: string;
  status: 'new' | 'confirmed' | 'done';
  created_at: string;
}

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [treatments, setTreatments] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);

  const SERVICE_LABELS: Record<string, string> = {
    hair: t('admin.service_hair'),
    dental: t('admin.service_dental'),
    botox: t('admin.service_botox'),
    combined: t('admin.service_combined'),
    consultation: t('admin.service_consultation'),
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }

    const loadAll = async () => {
      setLoading(true);
      try {
        // Load bookings from Supabase
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select('*')
          .order('booking_date', { ascending: true });

        if (bookingError) {
          console.error('Dashboard: bookings error:', bookingError);
        } else {
          setBookings(
            (bookingData || []).map((b) => ({
              id: String(b.id),
              date: b.booking_date || '',
              time: b.booking_time || '',
              firstname: b.firstname || '',
              lastname: b.lastname || '',
              email: b.email || '',
              phone: b.phone || '',
              service: b.service || '',
              package: b.package || '',
              message: b.message || '',
              status: (b.status || 'new') as 'new' | 'confirmed' | 'done',
              created_at: b.created_at || '',
            }))
          );
        }
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }

      // Live counts from Supabase
      try {
        const [tr, tm, rv] = await Promise.all([getTreatments(), getTeam(), getReviews()]);
        setTreatments(tr.length);
        setTeamCount(tm.length);
        setReviewsCount(rv.length);
      } catch { /* ignore */ }
    };

    loadAll();
  }, [navigate]);

  const newBookings = bookings.filter((b) => b.status === 'new').length;
  const confirmedBookings = bookings.filter((b) => b.status === 'confirmed').length;
  const doneBookings = bookings.filter((b) => b.status === 'done').length;
  const todayStr = new Date().toISOString().split('T')[0];
  const todayBookings = bookings.filter((b) => b.date === todayStr);
  const upcomingBookings = bookings
    .filter((b) => b.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${days[date.getDay()]}, ${parseInt(d)}. ${months[parseInt(m) - 1]}`;
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; classes: string }> = {
      new: { label: t('admin.status_new'), classes: 'bg-accent-100 text-accent-800' },
      confirmed: { label: t('admin.status_confirmed'), classes: 'bg-green-100 text-green-700' },
      done: { label: t('admin.status_done'), classes: 'bg-foreground-100 text-foreground-500' },
    };
    const c = config[status] || config.new;
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-label ${c.classes}`}>{c.label}</span>;
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-6 py-6 space-y-6">
      {/* ── Welcome ──────────────────────────────── */}
      <div>
        <h1 className="font-heading text-xl font-bold text-foreground-900">{t('admin.dashboard_title')}</h1>
        <p className="mt-0.5 text-xs text-foreground-500 font-body">
          {t('admin.dashboard_welcome')}
        </p>
      </div>

      {/* ── Stats Grid ────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: t('admin.stat_bookings'), value: bookings.length, sub: `${newBookings} ${t('admin.status_new')}`, icon: 'ri-calendar-check-line', color: 'primary' },
          { label: t('admin.status_new'), value: newBookings, sub: t('admin.stat_bookings_sub', { count: newBookings }), icon: 'ri-inbox-line', color: 'accent', highlight: newBookings > 0 },
          { label: t('admin.stat_confirmed'), value: confirmedBookings, sub: t('admin.stat_confirmed_sub'), icon: 'ri-check-double-line', color: 'green' },
          { label: t('admin.status_done'), value: doneBookings, sub: t('admin.stat_today_sub'), icon: 'ri-checkbox-circle-line', color: 'foreground' },
          { label: t('admin.stat_treatments'), value: treatments, sub: t('admin.stat_treatments_sub'), icon: 'ri-price-tag-3-line', color: 'secondary' },
          { label: t('admin.stat_team'), value: teamCount, sub: t('admin.stat_team_sub'), icon: 'ri-team-line', color: 'foreground' },
          { label: t('admin.stat_reviews'), value: reviewsCount, sub: t('admin.stat_reviews_sub'), icon: 'ri-chat-quote-line', color: 'accent' },
        ].map((stat) => {
          const colorMap: Record<string, { bg: string; text: string }> = {
            primary: { bg: 'bg-primary-100', text: 'text-primary-700' },
            green: { bg: 'bg-green-100', text: 'text-green-700' },
            accent: { bg: 'bg-accent-100', text: 'text-accent-700' },
            secondary: { bg: 'bg-secondary-100', text: 'text-secondary-700' },
            foreground: { bg: 'bg-foreground-100', text: 'text-foreground-700' },
          };
          const cm = colorMap[stat.color];
          return (
            <div
              key={stat.label}
              className={`p-4 rounded-xl border bg-background-50 transition-all ${
                stat.highlight ? 'border-accent-300 ring-1 ring-accent-200' : 'border-background-200/70'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${cm.bg} ${cm.text} mb-2`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground-900">{stat.value}</p>
              <p className="text-[10px] text-foreground-500 font-label uppercase tracking-[0.08em]">
                {stat.label} <span className="text-foreground-400 font-normal normal-case">· {stat.sub}</span>
              </p>
            </div>
          );
        })}
      </div>

      {/* ── Two Column Layout ──────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Bookings */}
        <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-todo-line text-foreground-500 text-sm"></i>
              <h2 className="font-label text-sm font-semibold text-foreground-900">{t('admin.upcoming_title')}</h2>
              <span className="text-[10px] text-foreground-400 font-body">({upcomingBookings.length})</span>
            </div>
            <Link
              to="/admin/bookings"
              className="text-xs text-primary-600 hover:text-primary-700 font-label no-underline whitespace-nowrap"
            >
              {t('admin.view_all')}
            </Link>
          </div>

          {loading ? (
            <div className="px-5 py-8 text-center">
              <i className="ri-loader-4-line animate-spin text-lg text-foreground-400"></i>
              <p className="mt-2 text-xs text-foreground-400 font-body">{t('admin.loading', 'Laden...')}</p>
            </div>
          ) : (
            <>
              {todayBookings.length > 0 && (
                <div className="px-5 pt-3 pb-1">
                  <p className="text-[10px] font-label uppercase tracking-[0.12em] text-foreground-400 mb-2">{t('admin.today_label')}</p>
                  {todayBookings.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-lg hover:bg-background-50 cursor-pointer transition-colors border-b border-background-100 last:border-0"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                        <span className="text-xs font-label font-bold">{b.time}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-label font-semibold text-foreground-900 truncate">{b.firstname} {b.lastname}</p>
                        <p className="text-[11px] text-foreground-500 font-body truncate">{SERVICE_LABELS[b.service] || b.service} · {b.package}</p>
                      </div>
                      {getStatusBadge(b.status)}
                    </div>
                  ))}
                </div>
              )}

              <div className="px-5 pt-1 pb-3">
                <p className="text-[10px] font-label uppercase tracking-[0.12em] text-foreground-400 mb-2 mt-3">{t('admin.upcoming_label')}</p>
                {upcomingBookings.filter((b) => b.date > todayStr).length === 0 ? (
                  <p className="text-xs text-foreground-400 font-body py-6 text-center">{t('admin.no_future_bookings')}</p>
                ) : (
                  upcomingBookings
                    .filter((b) => b.date > todayStr)
                    .slice(0, 5)
                    .map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-lg hover:bg-background-50 cursor-pointer transition-colors border-b border-background-100 last:border-0"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 flex-shrink-0">
                          <span className="text-[10px] font-label">{formatDate(b.date).split(',')[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-label font-semibold text-foreground-900 truncate">{b.firstname} {b.lastname}</p>
                          <p className="text-[11px] text-foreground-500 font-body truncate">{b.time} · {SERVICE_LABELS[b.service] || b.service}</p>
                        </div>
                        {getStatusBadge(b.status)}
                      </div>
                    ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Right: Quick Links */}
        <div className="space-y-4">
          {/* System Status */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-4 py-3 border-b border-background-100 flex items-center gap-2">
              <i className="ri-shield-check-line text-foreground-500 text-sm"></i>
              <h2 className="font-label text-sm font-semibold text-foreground-900">{t('admin.system_status', 'Systemstatus')}</h2>
            </div>
            <div className="px-4 py-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-body">
                  <span className="text-foreground-600">{t('admin.status_database', 'Datenbank')}</span>
                  <span className="flex items-center gap-1 text-green-600">
                    <i className="ri-checkbox-circle-line text-xs"></i>
                    {t('admin.status_connected', 'Verbunden')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs font-body">
                  <span className="text-foreground-600">{t('admin.status_bookings', 'Buchungen')}</span>
                  <span className="text-foreground-900 font-label">{bookings.length} {t('admin.entries', 'Einträge')}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-body">
                  <span className="text-foreground-600">{t('admin.status_treatments', 'Behandlungen')}</span>
                  <span className="text-foreground-900 font-label">{treatments} {t('admin.entries', 'Einträge')}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-body">
                  <span className="text-foreground-600">{t('admin.status_team', 'Team')}</span>
                  <span className="text-foreground-900 font-label">{teamCount} {t('admin.entries', 'Einträge')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
            <h2 className="font-label text-xs font-semibold text-foreground-900 mb-3">{t('admin.quick_title')}</h2>
            <div className="space-y-1.5">
              {[
                { label: t('admin.quick_home'), path: '/admin/pages-content', icon: 'ri-home-4-line', color: 'primary' },
                { label: t('admin.quick_bookings'), path: '/admin/bookings', icon: 'ri-calendar-check-line', color: 'accent' },
                { label: t('admin.quick_pricing'), path: '/admin/treatments', icon: 'ri-price-tag-3-line', color: 'secondary' },
                { label: t('admin.quick_team'), path: '/admin/team-manage', icon: 'ri-team-line', color: 'foreground' },
              ].map((link) => {
                const cm: Record<string, string> = {
                  primary: 'bg-primary-100 text-primary-600',
                  accent: 'bg-accent-100 text-accent-600',
                  secondary: 'bg-secondary-100 text-secondary-600',
                  foreground: 'bg-foreground-100 text-foreground-600',
                };
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer no-underline group"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${cm[link.color]} group-hover:opacity-80 transition-opacity`}>
                      <i className={`${link.icon} text-sm`}></i>
                    </div>
                    <span className="text-xs font-label text-foreground-700">{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}